import { action, makeObservable, observable, runInAction } from 'mobx';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import {
  RESEND_INDICATION_TTL_TIMER,
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethod,
} from '../utils/constants';
import { ERROR_CODES, extractServerErrorCode } from '../utils/errorHandler';
import { AuthenticateByCodeStore } from './authenticateByCode';
import { PRESETS } from '../utils/presets';
import type { RootStore } from './root';

/**
 * The error code of a failed IAM email-code request. Some Verify replies carry
 * an expired challenge only in the error data (as login 2FA does), so the
 * expiry is read from there too.
 */
const emailCodeErrorCode = (error: unknown): string =>
  (error as any)?.errorData === ERROR_CODES.TOKEN_EXPIRED_IAM
    ? ERROR_CODES.TOKEN_EXPIRED_IAM
    : extractServerErrorCode(error);

/** The challenge can't be used again: it expired, its flow moved past it, or it isn't this identity's. */
export const isInvalidChallenge = (error: unknown) =>
  [
    ERROR_CODES.TOKEN_EXPIRED_IAM,
    ERROR_CODES.EXPIRED_JWT_TOKEN,
    ERROR_CODES.INVALID_STATE_TOKEN,
  ].includes(emailCodeErrorCode(error));

/**
 * Where email-code signup and login can be offered at all: the Wix user type
 * (not product user types such as /hopp) on the Wix default page (no preset,
 * or WIX_2025), wix.com outside Studio, and a page whose logins use IAM, the
 * only API that sends a code.
 */
export const isEmailCodeAvailable = (rootStore: RootStore) =>
  !rootStore.userType &&
  (!rootStore.displayStore.presetKey ||
    rootStore.displayStore.presetKey === PRESETS.WIX_2025) &&
  !rootStore.isStudio &&
  rootStore.navigationStore.baseDomain === 'wix.com' &&
  rootStore.apiStore.canUseIAMLoginOnPage();

/** What starting the flow returned. */
export type FlowStart =
  /** Paused until the emailed code is verified. */
  | { kind: 'challenge'; stateToken?: string | null }
  /** Finished without a code: the session is already set. */
  | { kind: 'done'; payload: any }
  /** The account also needs a second factor. */
  | { kind: 'mfa'; payload: any };

/**
 * Starts an IAM flow with factor EMAIL and reads its reply: a pause on
 * `pausedOn` is the code challenge, SECOND_FACTOR_REQUIRED (when `allowMfa`)
 * hands off to 2FA, and a plain reply means the flow finished without a code.
 */
export const startEmailFactorFlow = async (
  send: () => Promise<{ payload?: object; data?: object } | undefined>,
  { pausedOn, allowMfa = false }: { pausedOn: string; allowMfa?: boolean },
): Promise<FlowStart> => {
  try {
    const data = await send();
    return { kind: 'done', payload: data?.payload ?? data?.data ?? {} };
  } catch (error: any) {
    if (error?.errorCode === pausedOn && error.payload?.stateToken) {
      return { kind: 'challenge', stateToken: error.payload.stateToken };
    }
    if (
      allowMfa &&
      error?.errorCode === ERROR_CODES.SECOND_FACTOR_REQUIRED &&
      error.payload
    ) {
      return { kind: 'mfa', payload: error.payload };
    }
    throw error;
  }
};

/**
 * The emailed-code step shared by email-code signup and login. Both start an
 * IAM flow that pauses until the code is sent to Verify; Verify without a code
 * on the same challenge sends a new code.
 *
 * Every request remembers the `requestVersion` it started with. Changing the
 * email or leaving the flow bumps it (`reset`), so a reply that arrives later
 * is ignored instead of overwriting the newer state (`isStale`).
 */
export abstract class EmailCodeStore extends AuthenticateByCodeStore {
  email = '';
  isLoading = false;
  hasSentCode = false;
  /** When the Resend link unlocks again; every sent code restarts the cooldown. */
  resendAvailableAt = 0;
  /** Set once the flow finished; the screen keeps its loader until the page is left. */
  isCompleting = false;
  protected requestVersion = 0;
  /** Why Verify spent the challenge, repeated if the code is submitted again. */
  private spentErrorCode?: string;

  /** Starts the flow for `this.email`, which sends the first code. */
  protected abstract startFlow(version: number): Promise<FlowStart>;
  /** The flow finished and the session is set: leave the page. */
  protected abstract finish(payload: any): void;
  /** The backend wants a CAPTCHA before it sends a code: show it to the user. */
  protected abstract askForCaptcha(errorCode: string): void;
  abstract switchToPassword(): void;
  /** Change email: back to the step where the email is entered. */
  abstract backToEmail(): void;

  /** The account needs a second factor: after the code, or straight from the start. */
  protected continueWithMfa(_payload: any) {
    this.handleError(ERROR_CODES.GENERAL_ERROR_CODE);
  }
  protected onReset() {}

  /** The token of the open challenge, which Verify and Resend send. */
  protected get challenge(): string | undefined {
    return this.stateToken;
  }

  protected set challenge(token: string | undefined) {
    this.stateToken = token;
  }

  constructor(rootStore: RootStore) {
    super(rootStore);
    this.currentTwoFAMethod = TwoFactorAuthMethods.Email;
    makeObservable(this, {
      email: observable,
      stateToken: observable,
      isLoading: observable,
      hasSentCode: observable,
      resendAvailableAt: observable,
      isCompleting: observable,
      setAuthParams: action,
      reset: action.bound,
      handleError: action,
      verify: action.bound,
      resendCode: action.bound,
    });
  }

  setAuthParams(payload: { stateToken?: string | null }) {
    this.challenge = payload.stateToken ?? undefined;
    this.spentErrorCode = undefined;
    this.hasSentCode = Boolean(this.challenge);
    if (this.hasSentCode) {
      this.resendAvailableAt = Date.now() + RESEND_INDICATION_TTL_TIMER;
    }
    this.clear();
  }

  /**
   * Sends a code: the first one, and the Resend link. Returns the error code
   * when nothing was sent. A failed resend restarts the cooldown and shows the
   * error on the code field; a failed first send (`lockOnFailure: false`)
   * leaves both to the caller. A CAPTCHA refusal is always handled here: the
   * challenge is dropped and the password form shows the CAPTCHA.
   */
  async resendCode({
    lockOnFailure = true,
  }: {
    phoneDeliveryMethod?: TwoFactorAuthPhoneDeliveryMethod;
    lockOnFailure?: boolean;
  } = {}): Promise<string | undefined> {
    if (this.isLoading || !this.enableAccess()) {
      return ERROR_CODES.GENERAL_ERROR_CODE;
    }
    this.isLoading = true;
    const version = this.requestVersion;
    let sendError: string | undefined;
    try {
      await this.sendCode(version);
    } catch (error) {
      sendError = emailCodeErrorCode(error);
      if (!this.isStale(version)) {
        this.rootStore.modalModeHandlerStore.handleErrorReport(sendError);
      }
    } finally {
      this.endRequest(version);
    }
    if (sendError && !this.isStale(version)) {
      this.showSendError(sendError, lockOnFailure);
    }
    return sendError;
  }

  async verify({ code }: { code: string }) {
    if (this.isLoading) {
      return;
    }
    if (this.codeField.value !== code) {
      this.codeField.setValue(code);
    }
    // The code field's own rules: six digits.
    this.codeField.triggerValidator();
    if (!this.codeField.isValid) {
      this.codeField.markFieldAsDirty();
      return;
    }
    const challenge = this.challenge;
    if (!challenge) {
      // A sent code whose challenge expired: say so again. With nothing sent
      // yet, Resend is the only way forward.
      if (this.hasSentCode) {
        this.handleError(this.spentErrorCode ?? ERROR_CODES.TOKEN_EXPIRED_IAM);
      }
      return;
    }
    this.isLoading = true;
    const version = this.requestVersion;
    try {
      const response = await this.rootStore.apiStore.verifyCodeAndLoginWithIAM({
        stateToken: challenge,
        twoFAMethod: TwoFactorAuthMethods.Email,
        twoFAToken: '',
        code,
      });
      if (this.isStale(version)) {
        return;
      }
      const payload = response?.data;
      if (response.success && payload?.state === StateType.MFA_CHALLENGE) {
        this.continueWithMfa(payload);
      } else if (response.success && payload?.state === StateType.SUCCESS) {
        this.finish(payload);
      } else {
        this.handleError(ERROR_CODES.GENERAL_ERROR_CODE);
      }
    } catch (error) {
      if (this.isStale(version)) {
        return;
      }
      const errorCode = emailCodeErrorCode(error);
      if (isInvalidChallenge(error)) {
        runInAction(() => {
          this.spendChallenge();
          this.spentErrorCode = errorCode;
        });
      }
      this.handleError(errorCode);
    } finally {
      this.endRequest(version);
    }
  }

  /** A reply to a request that a newer one (or a reset) has replaced. */
  protected isStale(version: number) {
    return version !== this.requestVersion;
  }

  /** Forgets the challenge and any reply still on its way. */
  reset() {
    this.requestVersion++;
    this.email = '';
    this.stateToken = undefined;
    this.hasSentCode = false;
    this.resendAvailableAt = 0;
    this.isLoading = false;
    this.isCompleting = false;
    this.spentErrorCode = undefined;
    this.clear();
    this.onReset();
  }

  /** Resends on the open challenge, or starts the flow over when there is none. */
  private async sendCode(version: number) {
    if (this.challenge) {
      const stateToken = await this.sendNewCodeOnChallenge(version);
      if (this.isStale(version)) {
        return;
      }
      if (stateToken) {
        this.setAuthParams({ stateToken });
        return;
      }
    }
    const start = await this.startFlow(version);
    if (this.isStale(version)) {
      return;
    }
    if (start.kind === 'mfa') {
      this.continueWithMfa(start.payload);
    } else if (start.kind === 'done') {
      this.finish(start.payload);
    } else {
      this.setAuthParams({ stateToken: start.stateToken });
    }
  }

  /**
   * Asks the open challenge for a new code. Resolves without a token when the
   * challenge is spent, so the caller starts the flow over.
   */
  private async sendNewCodeOnChallenge(version: number) {
    const stateToken = this.challenge!;
    try {
      const response = await this.rootStore.apiStore.sendNewEmailCode(
        stateToken,
      );
      return response.stateToken || stateToken;
    } catch (error) {
      if (!isInvalidChallenge(error)) {
        throw error;
      }
      if (!this.isStale(version)) {
        runInAction(() => this.spendChallenge());
      }
      return undefined;
    }
  }

  private spendChallenge() {
    this.challenge = undefined;
  }

  private showSendError(sendError: string, lockOnFailure: boolean) {
    if (this.rootStore.captchaStore.isCaptchaServerError(sendError)) {
      // Drop the challenge so that, once the CAPTCHA is solved, the next send
      // starts a new one with it.
      runInAction(() => {
        this.challenge = undefined;
        this.hasSentCode = false;
      });
      this.askForCaptcha(sendError);
      return;
    }
    if (lockOnFailure) {
      runInAction(() => {
        this.resendAvailableAt = Date.now() + RESEND_INDICATION_TTL_TIMER;
        this.showCodeFieldError(sendError);
      });
    }
  }

  /**
   * Reports a code error and shows it on the code field. Unlike the base
   * class, it reports no Panorama interaction.
   */
  handleError(errorCode: string) {
    this.rootStore.modalModeHandlerStore.handleErrorReport(errorCode);
    this.showCodeFieldError(errorCode);
  }

  private endRequest(version: number) {
    if (!this.isStale(version) && !this.isCompleting) {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}
